/**
 * Body-map regions for shared Wound Assessment.
 * Clicking a region toggles it into `woundMapSites` and updates `woundLocation`.
 */

/** @typedef {{ id: string, side: 'front'|'back', label: string, cx: number, cy: number, r?: number }} WoundMapRegion */

/** Front silhouette viewBox 0 0 120 280 */
export const WOUND_MAP_FRONT_REGIONS = /** @type {WoundMapRegion[]} */ ([
  { id: "front-head", side: "front", label: "Head (front)", cx: 60, cy: 22, r: 14 },
  { id: "front-neck", side: "front", label: "Neck (front)", cx: 60, cy: 48, r: 8 },
  { id: "front-chest", side: "front", label: "Chest", cx: 60, cy: 78, r: 16 },
  { id: "front-abdomen", side: "front", label: "Abdomen", cx: 60, cy: 112, r: 14 },
  { id: "front-left-arm", side: "front", label: "Left arm (front)", cx: 22, cy: 88, r: 11 },
  { id: "front-right-arm", side: "front", label: "Right arm (front)", cx: 98, cy: 88, r: 11 },
  { id: "front-left-hand", side: "front", label: "Left hand", cx: 16, cy: 108, r: 8 },
  { id: "front-right-hand", side: "front", label: "Right hand", cx: 104, cy: 108, r: 8 },
  { id: "front-pelvis", side: "front", label: "Pelvis / groin", cx: 60, cy: 138, r: 12 },
  { id: "front-left-thigh", side: "front", label: "Left thigh (front)", cx: 46, cy: 168, r: 11 },
  { id: "front-right-thigh", side: "front", label: "Right thigh (front)", cx: 74, cy: 168, r: 11 },
  { id: "front-left-leg", side: "front", label: "Left leg (front)", cx: 42, cy: 210, r: 11 },
  { id: "front-right-leg", side: "front", label: "Right leg (front)", cx: 78, cy: 210, r: 11 },
  { id: "front-left-foot", side: "front", label: "Left foot", cx: 40, cy: 246, r: 9 },
  { id: "front-right-foot", side: "front", label: "Right foot", cx: 80, cy: 246, r: 9 },
]);

/** Back silhouette — same coordinates, mirrored clinical labels */
export const WOUND_MAP_BACK_REGIONS = /** @type {WoundMapRegion[]} */ ([
  { id: "back-head", side: "back", label: "Head (back)", cx: 60, cy: 22, r: 14 },
  { id: "back-neck", side: "back", label: "Neck (back)", cx: 60, cy: 48, r: 8 },
  { id: "back-upper-back", side: "back", label: "Upper back", cx: 60, cy: 78, r: 16 },
  { id: "back-lower-back", side: "back", label: "Lower back", cx: 60, cy: 112, r: 14 },
  { id: "back-left-arm", side: "back", label: "Left arm (back)", cx: 22, cy: 88, r: 11 },
  { id: "back-right-arm", side: "back", label: "Right arm (back)", cx: 98, cy: 88, r: 11 },
  { id: "back-left-hand", side: "back", label: "Left hand (back)", cx: 16, cy: 108, r: 8 },
  { id: "back-right-hand", side: "back", label: "Right hand (back)", cx: 104, cy: 108, r: 8 },
  { id: "back-buttocks", side: "back", label: "Buttocks", cx: 60, cy: 138, r: 12 },
  { id: "back-left-thigh", side: "back", label: "Left thigh (back)", cx: 46, cy: 168, r: 11 },
  { id: "back-right-thigh", side: "back", label: "Right thigh (back)", cx: 74, cy: 168, r: 11 },
  { id: "back-left-leg", side: "back", label: "Left leg (back)", cx: 42, cy: 210, r: 11 },
  { id: "back-right-leg", side: "back", label: "Right leg (back)", cx: 78, cy: 210, r: 11 },
  { id: "back-left-foot", side: "back", label: "Left heel / foot", cx: 40, cy: 246, r: 9 },
  { id: "back-right-foot", side: "back", label: "Right heel / foot", cx: 80, cy: 246, r: 9 },
]);

export const WOUND_MAP_ALL_REGIONS = [
  ...WOUND_MAP_FRONT_REGIONS,
  ...WOUND_MAP_BACK_REGIONS,
];

const REGION_BY_ID = new Map(WOUND_MAP_ALL_REGIONS.map((r) => [r.id, r]));
const REGION_BY_LABEL = new Map(
  WOUND_MAP_ALL_REGIONS.map((r) => [r.label.toLowerCase(), r]),
);

export function getWoundMapRegion(id) {
  return REGION_BY_ID.get(id) || null;
}

export function labelsFromWoundMapSites(siteIds = []) {
  return (Array.isArray(siteIds) ? siteIds : [])
    .map((id) => REGION_BY_ID.get(id)?.label)
    .filter(Boolean);
}

/** Build comma-separated location text from selected map sites. */
export function woundLocationFromSites(siteIds = []) {
  return labelsFromWoundMapSites(siteIds).join(", ");
}

/**
 * Best-effort restore of map site ids from a free-text location string
 * (legacy rows that only had woundLocation).
 */
export function woundMapSitesFromLocationText(locationText = "") {
  const raw = String(locationText || "").trim();
  if (!raw) return [];
  const parts = raw
    .split(/[,;/|]+/)
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const ids = [];
  parts.forEach((part) => {
    const exact = REGION_BY_LABEL.get(part);
    if (exact) {
      ids.push(exact.id);
      return;
    }
    // Fuzzy: "right leg" matches "Right leg (front)" preferentially.
    const fuzzy = WOUND_MAP_ALL_REGIONS.find((r) => {
      const label = r.label.toLowerCase();
      return label.includes(part) || part.includes(label.replace(/\s*\(.*?\)\s*/g, "").trim());
    });
    if (fuzzy) ids.push(fuzzy.id);
  });
  return [...new Set(ids)];
}

export function toggleWoundMapSite(siteIds = [], regionId) {
  const set = new Set(Array.isArray(siteIds) ? siteIds : []);
  if (set.has(regionId)) set.delete(regionId);
  else set.add(regionId);
  return [...set];
}
